import { useEffect, useState } from "react";
import { profileService } from "../../../Services/profile.service";
import { usersService } from "../../../Services/users.service";
import { useDispatch } from "react-redux";
import Spinner from "../../Components/Spinner";
import ChangePhoto from "../../Components/ChangePhoto/ChangePhoto";
import "./Profile.css";
import { useTranslation } from "react-i18next";

export default function Profile() {
  const { t } = useTranslation();
  
  const [user, setUser] = useState(null);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [showToastError, setShowToastError] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [currentAvatarPreview, setCurrentAvatarPreview] = useState(null);
  const [showToastAvatar, setShowToastAvatar] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch({ type: "title", value: t("PROFILE.TITLE") });
  }, [dispatch, t]);
  
  // eslint-disable-next-line 
  const userId = localStorage.getItem("ofv2-settings")
    ? JSON.parse(localStorage.getItem("ofv2-settings")).userId
    : null;

  const loadSavedAvatar = async () => {
    try {
      const blob = await usersService.fetchAvatarImage();
      if (blob) {
        const imageUrl = URL.createObjectURL(blob);
        setCurrentAvatarPreview(imageUrl);
      }
    } catch (e) {
      console.error(e);
    }
  };

  async function fetchUserData() {
    setLoading(true);
    setUser(null);
    try {
      const userData = await profileService.users();
      console.log("📊 Dados do usuário recebidos:", userData);
      setUser(userData);
      await loadSavedAvatar();
    } catch (error) {
      console.error("🚨 Erro ao buscar perfil:", error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (user === null) {
      fetchUserData();
    }
  }, [user, fetchUserData]);

  const formatPhoneNumber = (value) => {
    if (!value) return "";
    return value
      .replace(/\D/g, "") // Remove tudo que não for número
      .replace(/^(\d{2})(\d)/, "($1) $2") // Adiciona parênteses no DDD
      .replace(/(\d{5})(\d{4})$/, "$1-$2") // Adiciona o traço no lugar correto
      .replace(/(\d{4})(\d{4})$/, "$1-$2") // Ajusta para telefones fixos
      .slice(0, 15); // Limita o tamanho máximo do telefone
  };
  
  function formatToE164(phone) {
    if (!phone) return "";
    return phone.replace(/\D/g, "").replace(/^0?/, "");
  }
  
  const validateForm = () => {
    let newErrors = {};
  
    if (!user?.userName?.trim()) newErrors.userName = t("PROFILE.NAME_REQUIRED");
    if (!user?.email?.trim()) newErrors.email = t("PROFILE.EMAIL_REQUIRED");
    if (!user?.userPhone?.trim()) {
      newErrors.userPhone = t("PROFILE.PHONE_REQUIRED");
    } else {
      // Aceita telefones fixos (XXXX-XXXX) e celulares (9XXXX-XXXX)
      const phoneRegex = /^\(\d{2}\)\s?(?:9\d{4}|\d{4})-\d{4}$/;
      if (!phoneRegex.test(user.userPhone)) {
        newErrors.userPhone = t("PROFILE.PHONE_INVALID");
      }
    }
  
    return newErrors;
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value;

    if (name === "userPhone") {
      updatedValue = formatPhoneNumber(value);
    }

    setUser({ ...user, [name]: updatedValue });

    // Remover o erro ao corrigir o campo
    if (touched[name]) {
      setErrors((prevErrors) => ({ ...prevErrors, [name]: undefined }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched({ ...touched, [name]: true });

    const newErrors = validateForm();
    if (newErrors[name]) {
      setErrors((prevErrors) => ({ ...prevErrors, [name]: newErrors[name] }));
    }
  };

  const uploadAvatar = async () => {
    if (!avatarFile) return;
    
    try {
      const formData = new FormData();
      formData.append('AvatarFile', avatarFile);
      
      await usersService.avatarUpload(formData);
      await loadSavedAvatar();
      setAvatarFile(null);
      setShowToastAvatar(true);
      setTimeout(() => setShowToastAvatar(false), 3000);
    } catch (error) {
      console.error("Erro ao fazer upload do avatar:", error);
    }
  };

  const handleAvatarChange = (file) => {
    setAvatarFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Se só tem avatar para enviar, envia apenas o avatar
    if (avatarFile && (!user?.email?.trim() || !user?.userPhone?.trim())) {
      await uploadAvatar();
      return;
    }

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setUpdating(true);
    console.log("user to save", userId);
    try {
      await profileService.updateUsers({
        userTargetId: userId,
        userEmail: user?.email,
        username: user?.userName,
        userCellPhone: formatToE164(user?.userPhone),
        statusId: 1,
      });

      if (avatarFile) {
        await uploadAvatar();
      }

      setUser(null);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } catch (error) {
      console.error("Erro ao atualizar perfil:", error);
      setShowToastError(true);
      setTimeout(() => setShowToastError(false), 3000);
    } finally {
      setErrors({});
      setTouched({});
      setUpdating(false);
    }
  };

  if (loading && user === null) {
    return <Spinner />;
  }

  return (
    <div className="container pt-4">
      <form onSubmit={handleSubmit}>
        <div className="row">
          <div style={{width: 220}} className="col-3 mb-4">
            <h5 className="mb-3">{t("PROFILE.USER_PROFILE")}</h5>
            <ChangePhoto photo={handleAvatarChange} currentAvatar={currentAvatarPreview} />
          </div>
          <div className="col-lg-10">
            <div className="row">
              <div className="mb-3 col-6">
                <div className="input-group">
                  <span className="input-group-text text-primary">{t("COMMON.NAME")}</span>
                  <input
                    value={user?.userName || ""}
                    type="text"
                    className={`form-control ${
                     errors?.userName ? "is-invalid" : ""
                    }`}
                    name="userName"
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {errors.userName && (
                    <div className="invalid-feedback">{errors.userName}</div>
                  )}
                </div>
              </div>

              <div className="mb-3 col-6">
                <div className="input-group">
                  <span className="input-group-text text-primary">{t("LOGIN.EMAIL")}</span>
                  <input
                    value={user?.email || ""}
                    type="email"
                  className={`form-control ${
                    errors.email ? "is-invalid" : ""
                  }`}
                    name="email"
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {errors.email && (
                    <div className="invalid-feedback">
                      {errors.email}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="row">
              <div className="mb-3 col-6">
                <div className="input-group">
                  <span className="input-group-text text-primary">{t("PROFILE.PHONE")}</span>
                  <input
                    value={formatPhoneNumber(user?.userPhone) || ""}
                    type="text"
                    className={`form-control ${
                      errors.userPhone ? "is-invalid" : ""
                    }`}
                    name="userPhone"
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {errors.userPhone && (
                    <div className="invalid-feedback">
                      {errors.userPhone}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={updating}
                >
                  {updating ? t("PROFILE.UPDATING") : t("BUTTONS.SAVE")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>

      {showToast && (
        <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 10 }}>
          <div className="toast show bg-success text-white">
            <div className="toast-body">{t("PROFILE.UPDATED_SUCCESS")}</div>
          </div>
        </div>
      )}
      {showToastError && (
        <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 10 }}>
          <div className="toast show bg-danger text-white">
            <div className="toast-body">
              {t("PROFILE.UPDATE_ERROR")}
            </div>
          </div>
        </div>
      )}
      {showToastAvatar && (
        <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 10 }}>
          <div className="toast show bg-success text-white">
            <div className="toast-body">
              {t("PROFILE.AVATAR_UPDATED")}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}